import { Dispatch, SetStateAction } from 'react';
import { ColumnDef } from '@tanstack/react-table';

import { FieldValidationResult, RowValidationResult, TableViewRow, ValidationResultMap } from './types';

export function validateField(
  meta: any,
  value: any,
  row: TableViewRow,
  context: { readyToValidate?: boolean } = {},
): FieldValidationResult {
  if (typeof meta?.shouldValidate === 'function' && !meta.shouldValidate(row, context)) {
    return { error: false };
  }
  const isRequired = typeof meta?.required === 'function' ? meta.required(row, context) : Boolean(meta?.required);
  if (isRequired && (value === undefined || value === '' || !value?.toString().trim())) {
    return {
      error: true,
      message: meta.requiredMessage || 'This field is required',
    };
  }

  if (meta?.validate) {
    const result = meta.validate(value);

    if (typeof result === 'string') {
      return {
        error: true,
        message: result,
      };
    } else if (!result) {
      return {
        error: true,
        message: meta.validationMessage || 'Invalid value',
      };
    }
  }

  return { error: false };
}

export function validateRowAgainstColumns(
  row: TableViewRow,
  columns: ColumnDef<any, any>[],
  context: { readyToValidate?: boolean } = {},
): RowValidationResult {
  const result: RowValidationResult = {};

  columns.forEach((column) => {
    const columnId = column.id as string;
    const meta = column.meta || {};
    const value = row[columnId];
    result[columnId] = validateField(meta, value, row, context);
  });

  return result;
}

export function validateRowAndSetResult(
  row: TableViewRow,
  columns: ColumnDef<any, any>[],
  setValidationResult: Dispatch<SetStateAction<ValidationResultMap>>,
  context: { readyToValidate?: boolean } = {},
): boolean {
  const rowValidation = validateRowAgainstColumns(row, columns, context);
  const shouldSet = context.readyToValidate || Object.values(rowValidation).some((row) => row.error);

  if (shouldSet) {
    setValidationResult((prev) => ({
      ...prev,
      [row.id]: rowValidation,
    }));
  }

  return !Object.values(rowValidation).some((result) => result.error);
}

export function setIfValidRow(
  editRow: TableViewRow | null | undefined,
  columns: ColumnDef<any, any>[],
  setValidationResult: Dispatch<SetStateAction<ValidationResultMap>>,
  setNewRows: Dispatch<SetStateAction<TableViewRow[]>>,
  cancelEdit: () => void,
  callback?: (validRow: TableViewRow) => void,
) {
  if (!editRow) return false;

  const isValid = validateRowAndSetResult(editRow, columns, setValidationResult);
  if (!isValid) return false;

  callback?.(editRow);
  setNewRows((prev) => prev.filter((r) => r.id !== editRow.id));
  cancelEdit();
  return true;
}

export const resolveRowAction = (
  value: boolean | ((row: TableViewRow) => boolean) | undefined,
  row: TableViewRow,
): boolean => {
  if (typeof value === 'function') return value(row);
  return !!value;
};

export const resolveCellAttribute = (
  value: string | ((row: TableViewRow) => string) | undefined,
  row: TableViewRow,
): string => {
  if (typeof value === 'function') return value(row);
  return value ?? '';
};
